// @flow
import * as React from 'react';
import { Link, useRouteState, RouteContext } from '@ta/common.routing';
import { featureIsEnabled } from '@ta/common.features';
import type { TripModalDescriptor } from '@ta/trips.trip-types';

type Props = {|
  tripModalDescriptor: TripModalDescriptor,
  className?: string,
  onClick?: (SyntheticEvent<HTMLAnchorElement>) => mixed,
  children: React.Node,
|};

const useTargetValue = (): '_blank' | '_self' => {
  const restaurantPages = ['MobileRestaurants', 'Restaurants'];
  const attractionPages = ['MobileAttractions', 'Attractions'];
  const {
    route: { page },
  } = React.useContext(RouteContext);
  // _ideally_ the target value is consistently _blank or _self across the site
  const curatedTrips =
    (attractionPages.includes(page) && featureIsEnabled('curated_trips_attractions')) ||
    (restaurantPages.includes(page) && featureIsEnabled('curated_trips_restaurants'));
  return curatedTrips ? '_blank' : '_self';
};

// Do the check on module-instantiation time, instead of render since these can dynamically change
const isTripModalHomeFeatureEnabled = featureIsEnabled('trip_modal_home');

const TripModalLink = ({
  tripModalDescriptor: { isUser, href, navigator, isBoosted },
  onClick,
  className,
  children,
}: Props) => {
  const { to: toTripModal } = useRouteState({ tripModal: navigator });
  const target = useTargetValue();
  const {
    route: { page },
  } = React.useContext(RouteContext);
  const scrollType = navigator.scrollToReference?.type;
  const scrollId = navigator.scrollToReference?.id;
  const toTripDetail = { page: 'Trip', tripId: navigator.id, scrollType, scrollId, isBoosted };
  const openTripModal = (page !== 'Home' || isTripModalHomeFeatureEnabled) && !isUser;
  const to = openTripModal ? toTripModal : toTripDetail;
  if (isBoosted) {
    to.isBoosted = true;
  }
  // urls are in a weird state right now. Manually construct query params if an href is passed in
  let hrefWithParams =
    href && toTripDetail && navigator.scrollToReference
      ? `${href}?type=${navigator.scrollToReference.type}&id=${navigator.scrollToReference.id}`
      : href;

  if (isBoosted && hrefWithParams) {
    hrefWithParams += '&boosted=true';
  }

  return (
    <Link href={hrefWithParams} to={to} onClick={onClick} className={className} target={target}>
      {children}
    </Link>
  );
};

export default TripModalLink;
